import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Tag,
  Skeleton,
  Space,
  theme,
  Button,
  Empty,
} from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { departmentService } from '../../services/departmentService';
import { employeeService } from '../../services/employeeService';
import { leaveService } from '../../services/leaveService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import formatDateRo from '../../utils/formatDateRo';
import { nearestWeekday, addWeekdays } from '../../utils/weekdayUtils';
import logo from '../../images/logo.png';
import './Dashboard.css';

const { Title, Text, Paragraph } = Typography;
const { useToken } = theme;

const PENDING_PREVIEW_LIMIT = 5;
const PATH_DEPARTMENTS = '/departments';
const PATH_EMPLOYEES = '/employees';
const PATH_LEAVE_PENDING = '/leave';
const PATH_LEAVE_ALL = '/leave?status=ALL';
const PATH_CALENDAR = '/calendar';
const CALENDAR_EVENT_TYPE_LEAVE = 'LEAVE';
const LEAVE_STATUS_APPROVED = 'APPROVED';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, canApproveLeaveRequests, canAccessEmployees, canViewDashboard } = useAuth();
  const { token } = useToken();
  const { t } = useTranslation();
  const canViewLeaveStats =
    canApproveLeaveRequests?.() ||
    user?.roles?.some((role) => ['ROLE_ADMIN', 'ROLE_HR', 'ADMIN', 'HR'].includes(role));
  const canViewWhoIsOut =
    canViewDashboard?.() ||
    user?.roles?.some((role) => ['ROLE_ADMIN', 'ROLE_HR', 'ROLE_RECEPTION', 'ADMIN', 'HR', 'RECEPTION'].includes(role));
  const canViewEmployees =
    canAccessEmployees?.() ||
    user?.roles?.some((role) => ['ROLE_ADMIN', 'ROLE_HR', 'ADMIN', 'HR'].includes(role));
  const [stats, setStats] = useState({
    totalDepartments: 0,
    totalEmployees: 0,
    pendingLeaveRequests: 0,
    totalLeaveRequests: 0,
  });
  const [pendingPreview, setPendingPreview] = useState([]);
  const [outTodayEvents, setOutTodayEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewedDate, setViewedDate] = useState(() => nearestWeekday(new Date()));
  const [outDateLoading, setOutDateLoading] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [departments, employees, pendingRequests, allRequests] =
          await Promise.all([
            departmentService.getAll(),
            canViewEmployees ? employeeService.getAll() : Promise.resolve([]),
            canViewLeaveStats ? leaveService.getPendingRequests() : Promise.resolve([]),
            canViewLeaveStats ? leaveService.getAllRequests() : Promise.resolve([]),
          ]);

        setStats({
          totalDepartments: departments.length,
          totalEmployees: employees.length,
          pendingLeaveRequests: pendingRequests.length,
          totalLeaveRequests: allRequests.length,
        });

        const sortedPending = [...pendingRequests].sort(
          (a, b) => (b.id || 0) - (a.id || 0)
        );
        setPendingPreview(sortedPending.slice(0, PENDING_PREVIEW_LIMIT));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewLeaveStats, canViewEmployees]);

  useEffect(() => {
    if (!canViewWhoIsOut) return;
    const fetchOutForDate = async () => {
      setOutDateLoading(true);
      const dateStr = viewedDate.toISOString().split('T')[0];
      try {
        const events = await leaveService.getCalendarEvents(dateStr, dateStr);
        const approved = (events || []).filter(
          (event) =>
            event.type === CALENDAR_EVENT_TYPE_LEAVE &&
            event.status === LEAVE_STATUS_APPROVED
        );
        setOutTodayEvents(approved);
      } catch (error) {
        console.error('Error fetching out-today events:', error);
      } finally {
        setOutDateLoading(false);
      }
    };

    fetchOutForDate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedDate, canViewWhoIsOut]);

  const getUserRole = () => {
    const raw = user?.roles?.[0]?.replace('ROLE_', '');
    const known = ['ADMIN', 'HR', 'EMPLOYEE', 'RECEPTION'].includes(raw) ? raw : 'EMPLOYEE';
    return t(`dashboard.roles.${known}`);
  };

  const statCards = [
    {
      title: t('dashboard.totalDepartments'),
      value: stats.totalDepartments,
      icon: <TeamOutlined />,
      color: token.colorPrimary,
      show: true,
      path: PATH_DEPARTMENTS,
      ariaLabel: t('dashboard.cardAriaLabels.departments'),
    },
    {
      title: t('dashboard.totalEmployees'),
      value: stats.totalEmployees,
      icon: <UserOutlined />,
      color: token.colorSuccess,
      show: canViewEmployees,
      path: PATH_EMPLOYEES,
      ariaLabel: t('dashboard.cardAriaLabels.employees'),
    },
    {
      title: t('dashboard.pendingRequests'),
      value: stats.pendingLeaveRequests,
      icon: <ClockCircleOutlined />,
      color: token.colorWarning,
      show: canViewLeaveStats,
      path: PATH_LEAVE_PENDING,
      ariaLabel: t('dashboard.cardAriaLabels.pendingRequests'),
    },
    {
      title: t('dashboard.totalLeaveRequests'),
      value: stats.totalLeaveRequests,
      icon: <CalendarOutlined />,
      color: token.colorInfo,
      show: canViewLeaveStats,
      path: PATH_LEAVE_ALL,
      ariaLabel: t('dashboard.cardAriaLabels.totalLeaveRequests'),
    },
  ];

  const navigateTo = (path) => () => navigate(path);
  const navigateOnEnterOrSpace = (path) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(path);
    }
  };

  const handlePrevDay = () => setViewedDate((d) => addWeekdays(d, -1));
  const handleNextDay = () => setViewedDate((d) => addWeekdays(d, +1));

  const TODAY_DATE_STR = new Date().toISOString().split('T')[0];
  const viewedDateStr = viewedDate.toISOString().split('T')[0];
  const isViewingToday = viewedDateStr === TODAY_DATE_STR;

  return (
    <div className="dashboard">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="dashboard-header">
          <Space align="center" size="middle">
            <img src={logo} alt={t('dashboard.logoAlt')} className="dashboard-logo" />
            <div>
              <Title level={2} style={{ marginBottom: 8 }}>{t('dashboard.title')}</Title>
              <Space>
                <Text type="secondary">{t('dashboard.welcome', { username: user?.username })}</Text>
                <Tag color={token.colorPrimary}>{getUserRole()}</Tag>
              </Space>
            </div>
          </Space>
        </div>

        <Row gutter={[16, 16]}>
          {statCards.filter(card => card.show).map((card, index) => (
            <Col xs={24} sm={12} lg={6} key={card.title}>
              <Card
                className="stat-card stat-card--clickable"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={navigateTo(card.path)}
                onKeyDown={navigateOnEnterOrSpace(card.path)}
                role="button"
                tabIndex={0}
                aria-label={card.ariaLabel}
              >
                <Statistic
                  title={card.title}
                  value={card.value}
                  prefix={card.icon}
                  loading={loading}
                  valueStyle={{ color: card.color }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {(canViewLeaveStats || canViewWhoIsOut) && (
          <Row gutter={[16, 16]}>
            {canViewLeaveStats && (
              <Col xs={24} lg={canViewWhoIsOut ? 12 : 24}>
                <Card
                  title={t('dashboard.pendingPanel.title')}
                  className="dashboard-card"
                  data-testid="dashboard-pending-panel"
                  extra={
                    <Button type="link" onClick={navigateTo(PATH_LEAVE_PENDING)}>
                      {t('dashboard.pendingPanel.viewAll')}
                    </Button>
                  }
                >
                  {loading ? (
                    <Skeleton active />
                  ) : pendingPreview.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t('dashboard.pendingPanel.empty')}
                    />
                  ) : (
                    <div className="dashboard-list">
                      {pendingPreview.map((item) => (
                        <div
                          key={item.id}
                          className="dashboard-list-item"
                          onClick={navigateTo(PATH_LEAVE_PENDING)}
                          onKeyDown={navigateOnEnterOrSpace(PATH_LEAVE_PENDING)}
                          role="button"
                          tabIndex={0}
                          data-testid="dashboard-pending-item"
                        >
                          <ClockCircleOutlined
                            className="dashboard-list-item__icon"
                            style={{ color: token.colorWarning }}
                          />
                          <div className="dashboard-list-item__body">
                            <Text strong>{item.employeeName}</Text>
                            <div>
                              <Text type="secondary">
                                {t(`leave.form.types.${item.leaveType}`)}
                              </Text>
                              <Text type="secondary"> · </Text>
                              <Text type="secondary">
                                {formatDateRo(item.startDate)} - {formatDateRo(item.endDate)}
                              </Text>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </Col>
            )}
            {canViewWhoIsOut && (
              <Col xs={24} lg={canViewLeaveStats ? 12 : 24}>
                <Card
                  title={
                    <Space align="center" size={4}>
                      <Button
                        type="text"
                        size="small"
                        icon={<LeftOutlined />}
                        onClick={handlePrevDay}
                        aria-label={t('dashboard.outTodayPanel.prevDay')}
                      />
                      <Text>
                        {isViewingToday
                          ? t('dashboard.outTodayPanel.title')
                          : formatDateRo(viewedDateStr)}
                      </Text>
                      <Button
                        type="text"
                        size="small"
                        icon={<RightOutlined />}
                        onClick={handleNextDay}
                        aria-label={t('dashboard.outTodayPanel.nextDay')}
                      />
                    </Space>
                  }
                  className="dashboard-card"
                  data-testid="dashboard-out-today-panel"
                  extra={
                    <Button type="link" onClick={navigateTo(PATH_CALENDAR)}>
                      {t('dashboard.outTodayPanel.viewCalendar')}
                    </Button>
                  }
                >
                  {outDateLoading ? (
                    <Skeleton active />
                  ) : outTodayEvents.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t('dashboard.outTodayPanel.empty')}
                    />
                  ) : (
                    <div className="dashboard-list">
                      {outTodayEvents.map((item) => (
                        <div
                          key={item.id}
                          className="dashboard-list-item dashboard-list-item--static"
                          data-testid="dashboard-out-today-item"
                        >
                          <UserOutlined
                            className="dashboard-list-item__icon"
                            style={{ color: item.color || token.colorPrimary }}
                          />
                          <div className="dashboard-list-item__body">
                            <Text strong>{item.employeeName}</Text>
                            <div>
                              {item.departmentName && (
                                <>
                                  <Text type="secondary">{item.departmentName}</Text>
                                  {item.leaveType && <Text type="secondary"> · </Text>}
                                </>
                              )}
                              {item.leaveType && (
                                <Text type="secondary">
                                  {t(`leave.form.types.${item.leaveType}`)}
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </Col>
            )}
          </Row>
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title={t('dashboard.systemOverview')} className="dashboard-card">
              {loading ? (
                <Skeleton active />
              ) : (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>{t('dashboard.yourRole')}</Text>
                    <Tag color={token.colorPrimary}>{getUserRole()}</Tag>
                  </div>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {t('dashboard.systemDescription')}
                  </Paragraph>
                  <div>
                    <Text type="secondary">
                      {t('dashboard.departmentColors')}
                    </Text>
                  </div>
                </Space>
              )}
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default Dashboard;
